import { gatewayApi as api } from './gatewayApi';

// Interfaces
export interface Shift {
  Id?: number;
  Name: string;
  StartTime: string; // Format: "HH:mm" or ISO string
  EndTime: string; // Format: "HH:mm" or ISO string
  Active: boolean;
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface ShiftListResponse {
  data: Shift[];
  total: number;
}

export interface ShiftSearchParams {
  page?: number;
  pageSize?: number;
  search?: string;
  active?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// API Service
export const shiftsApi = {
  // CRUD Operations
  getAll: (params?: ShiftSearchParams): Promise<ShiftListResponse> => {
    // If no search params, use GET /shift for simple listing
    if (!params || Object.keys(params).length === 0) {
      return api.get('/shift').then((response: any) => ({
        data: response.data || response || [],
        total: response.data?.length || response?.length || 0,
      }));
    }
    // Otherwise use POST /shift/search for filtered search
    return api.post('/shift/search', params).then((response: any) => ({
      data: response.data || [],
      total: parseInt(response.headers?.['x-total-count'] || '0', 10),
    }));
  },

  getById: (id: number): Promise<Shift> =>
    api.get(`/shift/${id}`).then((response: any) => response.data || response),

  create: (shift: Omit<Shift, 'Id'>): Promise<Shift> =>
    api.post('/shift', shift).then((response: any) => response.data || response),

  update: (id: number, shift: Partial<Shift>): Promise<Shift> =>
    api.put('/shift', { ...shift, Id: id }).then((response: any) => response.data || response),

  delete: (id: number): Promise<{ success: boolean; message: string }> =>
    api.delete(`/shift/${id}`).then((response: any) => response.data || response),

  // Search and Filter
  search: (params: ShiftSearchParams): Promise<ShiftListResponse> =>
    api.post('/shift/search', params).then((response: any) => ({
      data: response.data || [],
      total: parseInt(response.headers?.['x-total-count'] || '0', 10),
    })),

  // Bulk Operations (Note: These endpoints may not exist yet, keeping for future use)
  bulkActivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/shift/bulk-activate', { ids }).then((response: any) => response.data || response),

  bulkDeactivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/shift/bulk-deactivate', { ids }).then((response: any) => response.data || response),

  bulkDelete: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.post('/shift/bulk-delete', { ids }).then((response: any) => response.data || response),


  // Export (Note: This endpoint may not exist yet, keeping for future use)
  exportToCSV: (params?: ShiftSearchParams): Promise<Blob> =>
    api.getBlob('/shift/export', { params }),

  // Validation
  validation: {
    validateShift: (shift: Partial<Shift>): string[] => {
      const errors: string[] = [];

      if (!shift.Name || shift.Name.trim().length === 0) {
        errors.push('Shift Name is required');
      } else if (shift.Name.trim().length < 2) {
        errors.push('Shift Name must be at least 2 characters long');
      } else if (shift.Name.trim().length > 100) {
        errors.push('Shift Name must be less than 100 characters');
      }

      if (!shift.StartTime) {
        errors.push('Start Time is required');
      } else {
        const startTime = new Date(`2000-01-01T${shift.StartTime}`);
        if (isNaN(startTime.getTime())) {
          errors.push('Start Time must be in valid time format (HH:mm)');
        }
      }

      if (!shift.EndTime) {
        errors.push('End Time is required');
      } else {
        const endTime = new Date(`2000-01-01T${shift.EndTime}`);
        if (isNaN(endTime.getTime())) {
          errors.push('End Time must be in valid time format (HH:mm)');
        }
      }

      if (shift.StartTime && shift.EndTime) {
        const startTime = new Date(`2000-01-01T${shift.StartTime}`);
        const endTime = new Date(`2000-01-01T${shift.EndTime}`);
        if (startTime >= endTime) {
          errors.push('End Time must be after Start Time');
        }
      }

      if (shift.Active === undefined || shift.Active === null) {
        errors.push('Active status is required');
      }

      return errors;
    },

    validateBulkOperation: (ids: number[]): string[] => {
      const errors: string[] = [];
      if (!ids || ids.length === 0) {
        errors.push('At least one Shift must be selected');
      }
      if (ids.length > 100) {
        errors.push('Cannot perform bulk operation on more than 100 items at once');
      }
      return errors;
    }
  }
};

export default shiftsApi; 